import { useEffect, useMemo, useRef } from 'react';

import { useSnackbar } from 'notistack';

import dayjsService from 'services/dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoContatoStatusEnum,
  atendimentosSelector,
  fetchAtendimentosFinalizados,
  FiltersHistoricoParams,
  resetAtendimentosFinalizados,
} from 'store/reducers/atendimentos';

import { ModalFilterOptionsType } from './ModalFilters';
import NewListaAtendimentos from './NewListaAtendimentos';

interface Props {
  appliedFilters: ModalFilterOptionsType;
  isFilter: boolean;
}

const toApiParams = (
  filters: ModalFilterOptionsType,
): FiltersHistoricoParams => {
  const participantId =
    filters.atendente?.id || filters.participantId || undefined;

  const contatoId = filters.contato?.id || filters.contatoId;

  return {
    contatoId,
    code: filters.code,
    participantId,
    dateFinishedStart: filters.rangeDate?.[0]
      ? dayjsService(filters.rangeDate[0]).format('YYYY-MM-DD')
      : undefined,
    dateFinishedEnd: filters.rangeDate?.[1]
      ? dayjsService(filters.rangeDate[1]).format('YYYY-MM-DD')
      : undefined,
    dateCreatedAtStart: filters.rangeDateCreatedAt?.[0]
      ? dayjsService(filters.rangeDateCreatedAt[0]).format('YYYY-MM-DD')
      : undefined,
    dateCreatedAtEnd: filters.rangeDateCreatedAt?.[1]
      ? dayjsService(filters.rangeDateCreatedAt[1]).format('YYYY-MM-DD')
      : undefined,
    channelCodes: filters.channelCodes?.length
      ? (filters.channelCodes as string[])
      : undefined,
    tagIds: filters.tagIds?.length ? (filters.tagIds as string[]) : undefined,
    message: filters.message || undefined,
    reasonId: filters.reasonId || undefined,
  };
};

function TabPanelAtendimentosFinalizados({ appliedFilters, isFilter }: Props) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const {
    atendimentosFinalizados,
    atendimentosFinalizadosCursor,
    loadingAtendimentosFinalizados,
  } = useAppSelector(atendimentosSelector);

  const paginationRequestRef = useRef<{ abort: () => void }>();

  const fetchNextAtendimentos = () => {
    const { afterCursor } = atendimentosFinalizadosCursor;
    if (!loadingAtendimentosFinalizados && afterCursor) {
      paginationRequestRef.current = dispatch(
        fetchAtendimentosFinalizados({
          ...toApiParams(appliedFilters),
          cursor: { afterCursor },
        }),
      );
    }
  };

  const apiParams = useMemo(
    () => toApiParams(appliedFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(appliedFilters)],
  );

  useEffect(() => {
    dispatch(resetAtendimentosFinalizados());
    const request = dispatch(fetchAtendimentosFinalizados(apiParams));

    request.unwrap().catch(error => {
      if (error?.name !== 'AbortError') {
        enqueueSnackbar('Falha ao carregar atendimentos');
      }
    });

    return () => {
      request.abort();
      paginationRequestRef.current?.abort();
    };
  }, [apiParams, dispatch, enqueueSnackbar]);

  useEffect(() => {
    return () => {
      dispatch(resetAtendimentosFinalizados());
    };
  }, [dispatch]);

  return (
    <NewListaAtendimentos
      status={AtendimentoContatoStatusEnum.FINISHED}
      atendimentos={atendimentosFinalizados}
      fetchNextAtendimentos={fetchNextAtendimentos}
      hasMore={Boolean(atendimentosFinalizadosCursor.afterCursor)}
      isLoading={loadingAtendimentosFinalizados}
      isFilter={isFilter}
    />
  );
}

export default TabPanelAtendimentosFinalizados;
